// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import GroupedLinesHeader from "@eisinc/App/Components/Cart/GroupedLinesHeading";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { parseBoolean } from "@eisinc/App/Utilities/helpers";
import { StepperContext } from "@eisinc/App/Widgets/CheckoutStepper/StepperContext";
import Tab from "@eisinc/MobiusOverrides/Tab";
import TabGroup from "@eisinc/MobiusOverrides/TabGroup";
import CartLineCardExpanded from "@eisinc/Overrides/Widgets/Cart/CartLineCardExpanded";
import { CartComponent, ExternalProps } from "@eisinc/Overrides/Widgets/Cart/CartLines";
import { shipmentDateTypeValues } from "@eisinc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitCarrierService";
import { SHIPS_ASAP } from "@eisinc/XDL/constants";
import { formatDate } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDefaultPageSize, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CartLinesListStyles } from "@insite/content-library/Components/CartLinesList";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    // EIS-4460: Retrieve cartId from checkoutShipping because it gets cleared in checkoutReviewAndSubmit when placing an order
    const { cartId } = state.pages.checkoutShipping;
    const oldCartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    // this is for the front-store side only, as it takes the current cart to view it but the zero-priced won't be
    // displayed neither attached with the order request.
    let cartState = {} as any;
    if (oldCartState.value && oldCartState.value.cartLines) {
        cartState = {
            ...oldCartState,
            value: {
                ...oldCartState.value,
                cartLines: oldCartState.value.cartLines.filter(line => line?.pricing?.unitNetPrice !== 0),
            },
        };
    }

    const settingsCollection = getSettingsCollection(state) as any;

    return {
        cartState,
        promotionsDataView,
        defaultPageSize: getDefaultPageSize(state),
        settingsCollection,
        scheduleOrderPageUrl: getPageLinkByPageType(state, "ScheduleOrderPage")?.url || "/ScheduleOrder",
        shipmentScheduleFromState: (state as CustomAppState).xCheckout.shipmentSchedule,
        shippingCostBreakdown: (state as CustomAppState).xCheckout.shippingCostBreakdown,
        displayTabbedProductList: parseBoolean(settingsCollection?.customSettings?.displayTabbedProductList),
        isMultipleScheduleSelected:
            (state as CustomAppState).xCheckout.shipmentSchedule === shipmentDateTypeValues.ScheduledDates,
    };
};

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitProductListStyles {
    centeringWrapper?: InjectableCss;
    orderLinesList?: CartLinesListStyles;
    // XNG-Changes
    container?: InjectableCss;
    headingText?: TypographyPresentationProps;
    spinner?: LoadingSpinnerProps;
    editShipmentLink?: LinkPresentationProps;
    titleWrapper?: InjectableCss;
    smallHeadingAndText?: SmallHeadingAndTextStyles;
    shippingCostWrapper?: InjectableCss;
}

export const productListStyles: CheckoutReviewAndSubmitProductListStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
};

const styles = productListStyles;

export const allProductsLabel = "All Products";

const CheckoutReviewAndSubmitProductList: FC<Props> = ({
    cartState,
    promotionsDataView,
    scheduleOrderPageUrl,
    shipmentScheduleFromState,
    settingsCollection,
    shippingCostBreakdown,
    displayTabbedProductList,
    isMultipleScheduleSelected,
}) => {
    const { currentStep } = useContext(StepperContext);
    const [groupedCartlinesByDate, setGroupedCartlinesByDate] = useState<Map<string, CartLineModel[]>>(() => new Map());
    const [currentTab, setCurrentTab] = useState("");

    React.useEffect(() => {
        if (
            parseBoolean(cartState.value?.properties?.hasScheduledDeliveries) &&
            shipmentScheduleFromState === shipmentDateTypeValues.ScheduledDates
        ) {
            groupByRequestedDate();
        } else {
            setGroupedCartlinesByDate(() => new Map());
        }
    }, [cartState.value?.properties?.hasScheduledDeliveries, shipmentScheduleFromState, cartState.value]);

    const groupByRequestedDate = () => {
        const cartLines = (cartState?.value?.cartLines as CartLineModel[]) || [];
        const groupedCartlines = new Map<string, CartLineModel[]>();

        groupedCartlines.set(allProductsLabel, [...cartLines]);
        groupedCartlines.set(SHIPS_ASAP, []);

        for (const item of cartLines) {
            let date = item.properties?.requestedByDate || SHIPS_ASAP;
            if (date !== SHIPS_ASAP) {
                date = formatDate(convertDateOnlyStringToDate(date) ?? null);
            }

            if (!groupedCartlines.has(date)) {
                groupedCartlines.set(date, []);
            }

            groupedCartlines.get(date)?.push(item);
        }

        const sortedDateEntries = [...groupedCartlines.entries()]
            .filter(([key]) => key !== allProductsLabel && key !== SHIPS_ASAP)
            .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

        const sortedGroupedCartLines = new Map<string, CartLineModel[]>();
        sortedGroupedCartLines.set(allProductsLabel, groupedCartlines.get(allProductsLabel)!);

        const asapLines = groupedCartlines.get(SHIPS_ASAP);
        if (asapLines && asapLines.length > 0) {
            sortedGroupedCartLines.set(SHIPS_ASAP, asapLines);
        } else {
            sortedGroupedCartLines.delete(SHIPS_ASAP);
        }

        for (const [key, value] of sortedDateEntries) {
            sortedGroupedCartLines.set(key, value);
        }

        setGroupedCartlinesByDate(sortedGroupedCartLines);
        setCurrentTab(allProductsLabel);
    };

    if (!cartState.value || !cartState.value.cartLines || !promotionsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    // Hide products list on pickup step
    if (currentStep === 2 && cartState?.value.fulfillmentMethod === FulfillmentMethod.PickUp) {
        return null;
    }

    const options: ExternalProps["options"] = ["editable", "inventory", "date", "badging"];
    if (currentStep === 4) {
        options.push("poNumber");
    }

    // XNG-Change
    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            setCurrentTab(tabKey);
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="CheckoutReviewAndSubmitProductList">
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.headingText}>
                    {cartState?.value.fulfillmentMethod === FulfillmentMethod.Ship
                        ? translate("Product Shipments")
                        : translate("Products")}
                </Typography>
                {parseBoolean(cartState?.value.properties.hasScheduledDeliveries) && (
                    <Link href={scheduleOrderPageUrl} {...styles.editShipmentLink}>
                        {translate("Edit Shipment Schedule")}
                    </Link>
                )}
            </StyledWrapper>

            {cartState?.value.fulfillmentMethod === FulfillmentMethod.Ship &&
                (!groupedCartlinesByDate.size || !displayTabbedProductList) && (
                    <SmallHeadingAndText
                        heading={translate("Total Shipping Cost")}
                        text={cartState?.value.properties?.shipping}
                        extendedStyles={styles.smallHeadingAndText}
                    />
                )}
            {groupedCartlinesByDate.size > 0 && displayTabbedProductList ? (
                <>
                    <TabGroup current={currentTab} onTabChange={onTabChange} displayAsDropDownOnMobile={true}>
                        {[...groupedCartlinesByDate.entries()].map(([date, lines]) => {
                            return (
                                <Tab key={date} headline={date} tabKey={date}>
                                    <StyledWrapper {...styles.shippingCostWrapper}>
                                        {groupedCartlinesByDate.size > 0 && currentTab !== allProductsLabel && (
                                            <SmallHeadingAndText
                                                heading={translate("Items")}
                                                text={groupedCartlinesByDate.get(currentTab)?.length ?? 0}
                                                extendedStyles={styles.smallHeadingAndText}
                                            />
                                        )}

                                        <SmallHeadingAndText
                                            heading={
                                                shipmentScheduleFromState === shipmentDateTypeValues.ScheduledDates &&
                                                currentTab !== allProductsLabel
                                                    ? translate("Shipping Cost")
                                                    : translate("Total Shipping Cost")
                                            }
                                            text={
                                                shipmentScheduleFromState === shipmentDateTypeValues.ScheduledDates
                                                    ? shippingCostBreakdown?.[currentTab]?.AmountDisplay ||
                                                      cartState?.value.properties?.shipping
                                                    : cartState?.value.properties?.shipping
                                            }
                                            extendedStyles={styles.smallHeadingAndText}
                                        />
                                    </StyledWrapper>

                                    <GroupedLinesHeader
                                        editable={options?.includes("editable") && !options.includes("date")}
                                        showQtyOrderedShipped={options?.includes("qtyOrderedShipped")}
                                        showReqDeliveryDate={
                                            options?.includes("date") &&
                                            cartState?.value?.fulfillmentMethod === FulfillmentMethod.Ship
                                        }
                                    />

                                    {(lines as CartLineModel[]).map((line: CartLineModel) => {
                                        const promotions = promotionsDataView.value;

                                        const matchingPromotions = promotions
                                            ? promotions.filter(promo => promo.orderLineId === line.id)
                                            : [];
                                        return (
                                            <CartLineContext.Provider
                                                value={line}
                                                key={`${line.id}_${line.properties?.requestedByDate}`}
                                            >
                                                <CartLineCardExpanded
                                                    cart={cartState.value!}
                                                    promotions={matchingPromotions}
                                                    productSettings={settingsCollection.productSettings}
                                                    showLineNotes={true}
                                                    showRemoveAction={false}
                                                    productId={line?.productId}
                                                    showInventoryAvailability={options?.includes("inventory")}
                                                    showAddToList={options?.includes("addToList")}
                                                    showBadging={options?.includes("badging")}
                                                    editable={options?.includes("editable")}
                                                    editableQty={options?.includes("editableQty")}
                                                    showPONumber={options?.includes("poNumber")}
                                                    showDate={
                                                        options?.includes("date") &&
                                                        cartState?.value?.fulfillmentMethod === FulfillmentMethod.Ship
                                                    }
                                                    showQtyOrderedShipped={options?.includes("qtyOrderedShipped")}
                                                    showAddToCart={options?.includes("addToCart")}
                                                    showShipmentData={options?.includes("shipmentData")}
                                                    productIdAsId={options?.includes("productIdAsId")}
                                                    showQuotedPrice={options?.includes("showQuotedPrice")}
                                                    useModalForCertification={true}
                                                    dontShowCertification={options?.includes("dontShowCertification")}
                                                />
                                            </CartLineContext.Provider>
                                        );
                                    })}
                                </Tab>
                            );
                        })}
                    </TabGroup>
                </>
            ) : (
                <CartComponent
                    customCart={cartState.value}
                    customPromotion={promotionsDataView.value}
                    options={options}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitProductList),
    definition: {
        displayName: "Product List",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        group: "Checkout - Review & Submit",
    },
};

export default widgetModule;
